// Boundary views without a published primary key can return different WFS IDs
// for the same polygon in overlapping bounding-box requests.
export function stabilizeBoundaryFeatureIds(features, typeName, identities) {
  for (const feature of features) {
    const geometry = feature.getGeometry?.();
    if (!['Polygon', 'MultiPolygon'].includes(geometry?.getType?.())) continue;
    // Keep the complete coordinates: no hash collisions and no merging of
    // separate polygon parts that happen to share an administrative code.
    const polygons = geometry.getType() === 'Polygon'
      ? [geometry.getCoordinates()] : geometry.getCoordinates();
    const key = JSON.stringify(polygons.map(polygonKey).sort());
    if (!identities.has(key)) {
      identities.set(key, feature.get('s_source_id') ?? feature.getId() ?? `${typeName}:boundary:${key}`);
    }
    feature.setId(identities.get(key));
  }
  return features;
}
import GeoJSON from 'ol/format/GeoJSON.js';

function canonicalRing(ring) {
  const vertices = ring.map(vertex => JSON.stringify(vertex));
  if (vertices.length > 1 && vertices[0] === vertices.at(-1)) vertices.pop();
  if (!vertices.length) return '';
  const smallest = vertices.reduce((a, b) => a < b ? a : b);
  let canonical;
  for (let start = 0; start < vertices.length; start++) {
    if (vertices[start] !== smallest) continue;
    for (const direction of [1, -1]) {
      const candidate = Array.from({ length: vertices.length }, (_, index) =>
        vertices[(start + direction * index + vertices.length) % vertices.length]).join(',');
      if (canonical === undefined || candidate < canonical) canonical = candidate;
    }
  }
  return canonical;
}

function polygonKey(rings) {
  return JSON.stringify([canonicalRing(rings[0] || []), rings.slice(1).map(canonicalRing).sort()]);
}

// GeoServer may publish lnglat as the default geometry for nl_4pp.
// Prefer the polygon column returned among its secondary geometry properties.
export function preferBoundaryGeometry(input, typeName) {
  if (String(typeName).toLowerCase() !== 'geolinq:nl_4pp') return input;
  const data = typeof input === 'string' ? JSON.parse(input) : input;
  const normalize = feature => {
    let polygon = feature?.properties?.geometry;
    if (typeof polygon === 'string') {
      try { polygon = JSON.parse(polygon); } catch { polygon = null; }
    }
    if (!['Polygon', 'MultiPolygon'].includes(polygon?.type)) polygon = feature?.geometry;
    if (!['Polygon', 'MultiPolygon'].includes(polygon?.type)) {
      throw new Error('nl_4pp must provide a Polygon or MultiPolygon from geometry; lnglat points cannot be used as PC4 boundaries.');
    }
    const { geometry, lnglat, ...properties } = feature.properties || {};
    return { ...feature, geometry: polygon, geometry_name: 'geometry', properties };
  };
  return data?.type === 'FeatureCollection'
    ? { ...data, features: data.features.map(normalize) }
    : normalize(data);
}

// Filter against the polygon column while retaining all attributes. GeoServer
// rejects numeric postcode fields in propertyName projections, so do not enumerate that schema.
export function boundaryWfsSpatialFilter(typeName, extent) {
  return String(typeName).toLowerCase() === 'geolinq:nl_4pp'
    ? `CQL_FILTER=${encodeURIComponent(`BBOX(geometry,${extent.join(',')},'EPSG:3857')`)}`
    : `bbox=${extent.join(',')},EPSG:3857`;
}

export function createBoundaryGeoJSON(options = {}, typeName = '') {
  const format = new GeoJSON(options);
  const identities = new Map();
  const readFeatures = format.readFeatures.bind(format);
  format.readFeatures = (input, ...args) => stabilizeBoundaryFeatureIds(
    readFeatures(preferBoundaryGeometry(input, typeName), ...args), typeName, identities);
  return format;
}
